// What the brand cannot be saved without, and what each field has to look like
// once it holds anything. Everything not listed here is optional and free-form:
// the logo, the payment QR, the UPI details and the address line the design
// itself marks "(optional)".
//
// Each field's rule lives next to the normaliser that feeds it, so the shape
// the form accepts and the shape it stores can never drift apart.

import {
  digitsOf,
  emailError,
  mobileError,
  pincodeError,
} from "../../lib/fieldFormat";
import { BrandDetails } from "./types/brand.types";

export const REQUIRED_FIELDS = [
  "fullName",
  "brandName",
  "phone",
  "email",
  "state",
  "city",
  "pincode",
  "accountHolder",
  "accountNumber",
  "bankName",
] as const;

/**
 * Optional fields that are still checked once something has been typed into
 * them — leaving WhatsApp blank is fine, half a number in it is not. The two
 * UPI fields are the same bargain: a payout address that is nearly right is
 * worse than none at all, because nothing bounces to say so. The IFSC is the
 * same again: it can be filled in later, but a half-typed code is a payout
 * that goes nowhere.
 */
export const OPTIONAL_VALIDATED_FIELDS = [
  "whatsapp",
  "ifsc",
  "upiNumber",
  "upiId",
] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];
export type OptionalValidatedField = (typeof OPTIONAL_VALIDATED_FIELDS)[number];
export type ValidatedField = RequiredField | OptionalValidatedField;

export const VALIDATED_FIELDS = [
  ...REQUIRED_FIELDS,
  ...OPTIONAL_VALIDATED_FIELDS,
] as const;

export type BrandErrors = Partial<Record<ValidatedField, string>>;

/** Names used in "<field> is required", so the message reads like the label. */
const LABELS: Record<ValidatedField, string> = {
  fullName: "Full name",
  brandName: "Brand name",
  phone: "Phone number",
  whatsapp: "WhatsApp number",
  email: "Email address",
  state: "State",
  city: "City",
  pincode: "Pincode",
  accountHolder: "Account holder name",
  accountNumber: "Account number",
  bankName: "Bank name",
  ifsc: "IFSC code",
  upiNumber: "UPI number",
  upiId: "UPI ID",
};

/**
 * Longest brand name the documents can carry. It is a line on an invoice and
 * a meal plan, set at a fixed size on a page that does not reflow, so a name
 * past this stops being a heading and starts being a paragraph.
 */
export const BRAND_NAME_MAX = 50;

/** Four letters, a reserved `0`, then the six-character branch code. */
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;

/** A person's name as a bank holds it: letters, plus the joiners in one. */
const ACCOUNT_HOLDER_PATTERN = /^[A-Za-z][A-Za-z\s.'-]*[A-Za-z.]$/;

/** Starts with a letter; `&`, `.` and `-` cover "J & K Bank", "IDBI Bank". */
export const BANK_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9\s.&'-]*[A-Za-z0-9.]$/;

/**
 * A UPI address — `ravi.sharma@okhdfcbank`, `9876543210@ybl`. The handle after
 * the `@` is a bank's, and those are letters only; unlike an email there is no
 * dot and no TLD in it, which is exactly the mistake this catches.
 */
const UPI_ID_PATTERN = /^[a-z0-9][a-z0-9.\-_]{1,}@[a-z]{2,}$/;

const isRequired = (field: ValidatedField): field is RequiredField =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (field: ValidatedField, raw: string): string => {
  const value = raw.trim();

  // An empty optional field has nothing to be wrong about.
  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    // The field itself stops at `BRAND_NAME_MAX`, so this is for a name that
    // arrived some other way — saved before the rule existed, or pasted in by
    // a browser that ignores `maxlength`. It still has to be told, because it
    // is still a name the documents cannot set.
    case "brandName":
      return value.length > BRAND_NAME_MAX
        ? `Brand name cannot be longer than ${BRAND_NAME_MAX} characters`
        : "";
    case "email":
      return emailError(value);
    case "pincode":
      return pincodeError(value);
    // A UPI number is a mobile number registered against an account, so all
    // three answer to the same rule.
    case "phone":
    case "whatsapp":
    case "upiNumber":
      return mobileError(LABELS[field], value);
    case "accountHolder":
      // Digits get their own message: it is nearly always the account number
      // pasted a row too high, and "invalid name" would not say so.
      if (/\d/.test(value)) return "Account holder name cannot contain digits";
      return ACCOUNT_HOLDER_PATTERN.test(value)
        ? ""
        : "Enter the name as it appears on the account";
    case "accountNumber":
      return /^\d{9,18}$/.test(value.replace(/\s/g, ""))
        ? ""
        : "Account number must be 9 to 18 digits";
    case "bankName":
      return BANK_NAME_PATTERN.test(value) ? "" : "Enter a valid bank name";
    case "ifsc":
      return IFSC_PATTERN.test(value) ? "" : "Enter a valid IFSC code";
    case "upiId":
      return UPI_ID_PATTERN.test(value) ? "" : "Enter a valid UPI ID";
    default:
      return "";
  }
};

/** Only failing fields appear, so an empty object means the form is valid. */
export const validateBrandDetails = (details: BrandDetails): BrandErrors => {
  const errors: BrandErrors = {};

  VALIDATED_FIELDS.forEach((field) => {
    const message = fieldError(field, details[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

/* ── Input normalisers ─────────────────────────────────────────────────────
   The brand-only ones; phone, email and name casing are shared with the
   client form and live in `lib/fieldFormat`. Applied on every keystroke, so a
   field can only ever hold a shape the rules above recognise.              */

/** Digits only, stopped at the longest account number any Indian bank issues. */
export const normaliseAccountNumberInput = (raw: string): string =>
  digitsOf(raw).slice(0, 18);

/**
 * IFSC codes are printed and quoted in upper case, and hold nothing but
 * letters and digits — so a code pasted out of a statement with its spaces
 * still attached lands clean.
 */
export const normaliseIfscInput = (raw: string): string =>
  raw
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 11);

/** UPI addresses are case-insensitive and, like emails, hold no spaces. */
export const normaliseUpiIdInput = (raw: string): string =>
  raw.replace(/\s+/g, "").toLowerCase();
